import { useState, useEffect } from "react";
import { ActionMenu, ActionList, Button, Text, Stack, FormControl } from "@primer/react";
import { ChevronLeftIcon } from '@primer/octicons-react';
import PriceInput from "../../../../basic/inputs/PriceInput";

interface PriceInputProps {
    priceFilterOpened: boolean;
    setPriceFilterOpened: (value: boolean) => void;
    fromPrice: string;
    setFromPrice: (value: string) => void;
    toPrice: string;
    setToPrice: (value: string) => void;
    priceFormError: boolean;
    onDismiss: () => void;
    onApply: () => void;
}

const PriceFormModal: React.FC<PriceInputProps> = ({
    priceFilterOpened,
    setPriceFilterOpened,
    fromPrice,
    setFromPrice,
    toPrice,
    setToPrice,
    priceFormError,
    onDismiss,
    onApply
}) => {

    const [initialValues, setInitialValues] = useState({ fromPrice: '', toPrice: '' });

    useEffect(() => {
        if (priceFilterOpened) {
            setInitialValues({ fromPrice, toPrice });
        }
    }, [priceFilterOpened]);

    const dismiss = () => {
        // Reset vals
        setFromPrice(initialValues.fromPrice);
        setToPrice(initialValues.toPrice);

        onDismiss();
    }

    const clearForm = () => {
        setFromPrice('')
        setToPrice('')
    }

    const resetForm = () => {
        clearForm()
        setInitialValues({ fromPrice: '', toPrice: '' });
    }

    return (
        <ActionMenu open={priceFilterOpened} onOpenChange={setPriceFilterOpened}>
            <ActionMenu.Button className="w-8 h-8 right-[16px] absolute ml-auto invisible"></ActionMenu.Button>
            <ActionMenu.Overlay className="!w-[320px]">
                <ActionList>
                    <Stack justify="space-between" direction="horizontal">
                        <Stack direction="horizontal">
                            <Button
                                leadingVisual={ChevronLeftIcon}
                                variant='invisible'
                                onClick={dismiss}>
                                <Text className='block mb-[1.5px]'>По цене</Text>
                            </Button>
                        </Stack>
                    </Stack>
                    <Stack className='ml-4 mr-4'>
                        <Stack direction="horizontal">
                            <PriceInput
                                value={fromPrice}
                                onChange={setFromPrice}
                                type="from"
                                error={priceFormError}
                            />
                            <PriceInput
                                value={toPrice}
                                onChange={setToPrice}
                                type="to"
                                error={priceFormError}
                            />
                        </Stack>
                        {priceFormError && (
                            <FormControl.Validation variant="error" className='mt-[1px]'>Вы ввели некорректные значения</FormControl.Validation>
                        )}
                    </Stack>
                    <Stack direction="horizontal" gap='normal' justify='end' className="mt-[22px] mr-4">
                        <div></div>
                        <Button
                            disabled={(fromPrice.trim() || toPrice.trim()) ? false : true}
                            onClick={resetForm}
                            size='small'
                            variant="default">
                            Очистить
                        </Button>
                        <Button
                            onClick={onApply}
                            size='small'
                            variant="primary">
                            Применить
                        </Button>
                    </Stack>
                </ActionList>
            </ActionMenu.Overlay>
        </ActionMenu>
    )
}

const formatPriceRange = (fromPrice: string, toPrice: string): string => {
    const formatNumber = (num: string): string => {
        if (!num) return '';
        const cleanNum = num.replace(/\D/g, '');
        if (!cleanNum) return '';
        return cleanNum.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    };

    const formattedFrom = formatNumber(fromPrice);
    const formattedTo = formatNumber(toPrice);

    if (formattedFrom && formattedTo) {
        return `${formattedFrom}-${formattedTo} ₽`;
    } else if (formattedFrom) {
        return `от ${formattedFrom} ₽`;
    } else if (formattedTo) {
        return `до ${formattedTo} ₽`;
    }

    return '';
};

export { PriceFormModal, formatPriceRange }